import { gql, NetworkStatus, useQuery } from "@apollo/client";
import { shiftStreamClient } from "../../../graphql/client";

export const PAGE_SIZE = 50;

export const GET_EMAILS = gql`
  query getEmails(
    $sender: String
    $inbox: String
    $sourceAccountId: Int
    $roleId: Int
    $venueId: Int
    $state: EmailStateEnum
    $includeExpired: Boolean
    $includeUnmapped: Boolean
    $startDate: DateTimeISO
    $endDate: DateTimeISO
    $orderBy: EmailOrder
    $offset: Int!
    $limit: Int!
  ) {
    emails(
      inbox: $inbox
      sender: $sender
      sourceAccountId: $sourceAccountId
      roleId: $roleId
      venueId: $venueId
      state: $state
      includeExpired: $includeExpired
      includeUnmapped: $includeUnmapped
      startDate: $startDate
      endDate: $endDate
      orderBy: $orderBy
      offset: $offset
      limit: $limit
    ) {
      total

      data {
        id
        mailboxAddress
        sender
        receivedAt
        state
        errorMessage
        scrapeReasons
        expiresAt
        isExpiredManually
        stopped
        replyTo

        parsedClarify
        parseMessage
        parseExplanation

        isClarified

        contacts {
          id
          hasMapping
        }

        streams {
          id
          accountId
          state
          shiftId
          key
          noAlgo
          slots

          account {
            id
            provider
            name
          }

          shift {
            id
            startTime
            endTime
            sourceAccount {
              id
              accountName
            }
            role {
              id
              name
            }
            venue {
              id
              name
            }
            subvenue {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export type EmailOrder =
  | "ID"
  | "SENDER"
  | "INBOX"
  | "RECEIVED_AT"
  | "CLIENT"
  | "VENUE"
  | "ROLE"
  | "START";

export const useEmails = (
  {
    inbox,
    sender,
    sourceAccountId,
    roleId,
    venueId,
    state,
    startDate,
    endDate,
    includeExpired,
    includeUnmapped,
    orderBy,
  }: {
    inbox: string | null;
    sender: string | null;
    sourceAccountId: number | null;
    roleId: number | null;
    venueId: number | null;
    state: string | null;
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
    includeExpired: boolean;
    includeUnmapped: boolean;
    orderBy: EmailOrder;
  },
  page = 1,
) => {
  const safePage = Math.max(1, page);
  const offset = (safePage - 1) * PAGE_SIZE;

  const { data, previousData, loading, networkStatus, refetch } = useQuery(
    GET_EMAILS,
    {
      client: shiftStreamClient,
      variables: {
        inbox,
        sender,
        sourceAccountId,
        roleId,
        venueId,
        state,
        startDate,
        endDate,
        includeExpired,
        includeUnmapped,
        orderBy,
        offset,
        limit: PAGE_SIZE,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        throw error;
      },
    },
  );

  // Keep pagination metadata stable while the next page is loading.
  const resolvedData = data || previousData;

  const total = resolvedData?.emails?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    loading:
      loading ||
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.refetch,
    emails: resolvedData?.emails.data || [],
    total,
    totalPages,
    page: safePage,
    refetch,
  };
};
